import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import StatusPendingIconSvg from '../../../assets/icons/icon-referral-bonus-status-pending.svg?react';
import StatusSuccessIconSvg from '../../../assets/icons/icon-referral-bonus-status-success.svg?react';
import { normalizeNamePart } from '../../../shared/model';
import {
  REFERRAL_BONUS_STATUS_TEXT_KEY_BY_KIND,
  ReferralBonusParticipantStatusKind,
} from '../model';

type ReferralBonusParticipantCardProps = {
  emptyStateText: string;
  isError?: boolean;
  participants: ReadonlyArray<ReferralBonusParticipantCardItem>;
};

export type ReferralBonusParticipantCardItem = {
  firstName?: string;
  id: number;
  lastName?: string;
  statusKind: ReferralBonusParticipantStatusKind;
};

const List = styled.div.attrs({
  'data-ui': 'referral-bonus-participant-card',
})`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(10)};
`;

const Card = styled.section`
  border-radius: ${({ theme }) => theme.scale.px(11)};
  background: ${({ theme }) => theme.colors.surface.subtle};
  padding: ${({ theme }) => theme.scale.px(9)}
    ${({ theme }) => theme.scale.px(13)} ${({ theme }) => theme.scale.px(16)};
  box-sizing: border-box;
  display: grid;
  gap: ${({ theme }) => theme.scale.px(10)};
`;

const ParticipantName = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(19)};
`;

const StateCard = styled.section`
  border-radius: ${({ theme }) => theme.scale.px(11)};
  background: ${({ theme }) => theme.colors.surface.subtle};
  padding: ${({ theme }) => theme.scale.px(16)}
    ${({ theme }) => theme.scale.px(13)};
  box-sizing: border-box;
  font-size: ${({ theme }) => theme.scale.px(14)};
  line-height: ${({ theme }) => theme.scale.px(18)};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const StatusPill = styled.p<{
  $statusKind: ReferralBonusParticipantStatusKind;
}>`
  margin: 0;
  min-height: ${({ theme }) => theme.scale.px(47)};
  border-radius: ${({ theme }) => theme.scale.px(11)};
  padding: ${({ theme }) => theme.scale.px(14)}
    ${({ theme }) => theme.scale.px(15)};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.scale.px(8)};
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(19)};
  background: ${({ $statusKind }) =>
    $statusKind === 'completed' ? 'rgba(38, 161, 67, 0.5)' : '#c4c4c4'};
`;

const StatusPendingIcon = styled(StatusPendingIconSvg).attrs({
  'aria-hidden': true,
})`
  width: ${({ theme }) => theme.scale.px(20)};
  height: ${({ theme }) => theme.scale.px(20)};
  --stroke-0: ${({ theme }) => theme.colors.text.primary};
`;

const StatusSuccessIcon = styled(StatusSuccessIconSvg).attrs({
  'aria-hidden': true,
})`
  width: ${({ theme }) => theme.scale.px(20)};
  height: ${({ theme }) => theme.scale.px(20)};
  --stroke-0: ${({ theme }) => theme.colors.text.primary};
`;

function resolveParticipantName(
  participant: ReferralBonusParticipantCardItem,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  const firstName = normalizeNamePart(participant.firstName ?? '');
  const lastName = normalizeNamePart(participant.lastName ?? '');
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  if (fullName.length > 0) {
    return fullName;
  }

  if (firstName.length > 0) {
    return firstName;
  }

  if (lastName.length > 0) {
    return lastName;
  }

  return t('referralBonus.participantFallback', {
    suffix: String(participant.id).slice(-4).padStart(4, '0'),
  });
}

export function ReferralBonusParticipantCard({
  emptyStateText,
  isError = false,
  participants,
}: ReferralBonusParticipantCardProps) {
  const { t } = useTranslation();

  if (isError) {
    return (
      <List>
        <StateCard>{t('referralBonus.loadError')}</StateCard>
      </List>
    );
  }

  if (participants.length === 0) {
    return (
      <List>
        <StateCard>{emptyStateText}</StateCard>
      </List>
    );
  }

  return (
    <List>
      {participants.map(participant => {
        const statusText = t(
          REFERRAL_BONUS_STATUS_TEXT_KEY_BY_KIND[participant.statusKind],
        );

        return (
          <Card key={participant.id}>
            <ParticipantName>
              {resolveParticipantName(participant, t)}
            </ParticipantName>
            <StatusPill $statusKind={participant.statusKind}>
              {participant.statusKind === 'completed' ? (
                <StatusSuccessIcon />
              ) : (
                <StatusPendingIcon />
              )}
              {statusText}
            </StatusPill>
          </Card>
        );
      })}
    </List>
  );
}
